import { useCallback, useState, type CSSProperties } from "react";
import type { CatalogCard } from "../../api/client";
import { CardFlip } from "./CardFlip";
import { E32 } from "../../theme/endesga32";

interface CardMarqueeProps {
  cards: CatalogCard[];
  /** When true, the row scrolls in the reverse direction and slower. */
  reverse?: boolean;
  onCardClick: (card: CatalogCard) => void;
}

// Endless-scroll row: the list of cards is duplicated end-to-end and
// the wrapper translates `-50%` over the loop so the visual seam is
// imperceptible. Hover pauses the row (via the CSS class).
//
// Stuck state is scoped to this row. The row's own children include
// each card id twice (the duplication that makes the seamless loop
// work) and both copies must stick together so the scrolling seam
// doesn't reveal a still-face-down duplicate; but a card id that
// happens to appear in another marquee row sticks independently
// there. Per-row state gives us both behaviours for free.
export function CardMarquee({
  cards,
  reverse = false,
  onCardClick,
}: CardMarqueeProps) {
  const [stuckIds, setStuckIds] = useState<Set<string>>(() => new Set());

  const handleClick = useCallback(
    (card: CatalogCard) => {
      setStuckIds((prev) => {
        if (prev.has(card.id)) return prev;
        const next = new Set(prev);
        next.add(card.id);
        return next;
      });
      onCardClick(card);
    },
    [onCardClick],
  );

  if (cards.length === 0) return null;

  // Duplicate the list so the seamless -50% loop has a continuous
  // run of children to slide across.
  const duplicated = [...cards, ...cards];

  const trackStyle: CSSProperties = {
    display: "flex",
    gap: 16,
    width: "max-content",
  };

  return (
    <div style={{ position: "relative", overflow: "hidden" }}>
      <div
        className={
          reverse ? "doi-landing-marquee-reverse" : "doi-landing-marquee"
        }
        style={trackStyle}
      >
        {duplicated.map((card, i) => (
          <CardFlip
            key={`${card.id}-${i}`}
            card={card}
            stuck={stuckIds.has(card.id)}
            onClick={handleClick}
          />
        ))}
      </div>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: 120,
          pointerEvents: "none",
          background: `linear-gradient(90deg, ${E32.ink}, transparent)`,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: 120,
          pointerEvents: "none",
          background: `linear-gradient(270deg, ${E32.ink}, transparent)`,
        }}
      />
    </div>
  );
}
